import React from 'react';
import type { LucideIcon } from 'lucide-react';
import ParticleBackground from './ParticleBackground';

export type ErrorScreenAction = {
  label: string;
  href?: string;
  onClick?: () => void;
  primary?: boolean;
};

interface ErrorScreenProps {
  code: string;
  title: string;
  message: React.ReactNode;
  icon: LucideIcon;
  actions?: ErrorScreenAction[];
  details?: React.ReactNode;
}

const primaryClasses =
  'bg-gradient-to-r from-amber-400 to-amber-300 hover:from-amber-300 hover:to-amber-200 text-secondary-950 shadow-lg shadow-amber-900/40 hover:shadow-amber-400/40';
const secondaryClasses =
  'border border-secondary-700 bg-secondary-900/60 hover:bg-secondary-800 hover:border-secondary-600 text-white';

/**
 * Tela de erro em página inteira, no visual do login. Navega por href/history
 * em vez de useNavigate porque também é usada fora do <Router> (fallback do Sentry).
 */
const ErrorScreen: React.FC<ErrorScreenProps> = ({
  code,
  title,
  message,
  icon: Icon,
  actions = [],
  details,
}) => (
  <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-secondary-950 via-primary-950 to-secondary-950 px-4 py-10">
    <ParticleBackground backgroundColor="transparent" />

    <div className="pointer-events-none absolute -top-40 -left-40 w-[28rem] h-[28rem] rounded-full bg-primary-700/20 blur-3xl" />
    <div className="pointer-events-none absolute -bottom-40 -right-40 w-[28rem] h-[28rem] rounded-full bg-amber-500/10 blur-3xl" />

    <div
      className="relative z-10 w-full max-w-lg rounded-2xl border border-white/10 bg-secondary-950/70 backdrop-blur-xl p-8 lg:p-10 text-center shadow-2xl shadow-primary-950/40 animate-slide-up"
      style={{ animationFillMode: 'backwards' }}
    >
      <img
        src="/logo-codifica.png"
        alt="Logo Codifica"
        className="w-40 mx-auto mb-8 animate-fade-in"
        style={{ animationFillMode: 'backwards' }}
      />

      <div className="relative mb-6">
        <span
          aria-hidden="true"
          className="block text-7xl lg:text-8xl font-black tracking-tighter text-white/5 select-none"
        >
          {code}
        </span>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="rounded-full border border-amber-400/20 bg-amber-400/10 p-4">
            <Icon className="w-10 h-10 text-amber-400" />
          </div>
        </div>
      </div>

      <h1 className="text-2xl lg:text-3xl font-bold text-white mb-3">{title}</h1>
      <p className="text-sm text-secondary-400 leading-relaxed">{message}</p>

      {details && (
        <details className="mt-6 text-left">
          <summary className="cursor-pointer text-xs uppercase tracking-wide text-secondary-500 hover:text-amber-400 transition-colors">
            Detalhes técnicos
          </summary>
          <pre className="mt-2 max-h-40 overflow-auto rounded-lg bg-secondary-900/80 border border-secondary-800 p-3 text-[11px] text-secondary-400 whitespace-pre-wrap break-words">
            {details}
          </pre>
        </details>
      )}

      {actions.length > 0 && (
        <div className="mt-8 space-y-3">
          {actions.map((action) => {
            const className = `w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-200 ${
              action.primary ? primaryClasses : secondaryClasses
            }`;
            return action.href ? (
              <a key={action.label} href={action.href} className={className}>
                {action.label}
              </a>
            ) : (
              <button key={action.label} type="button" onClick={action.onClick} className={className}>
                {action.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  </div>
);

export default ErrorScreen;
